import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money, canonRodeoEvent } from "@/lib/ui";

const k = (e) => canonRodeoEvent(e);

export function useEnteredCounts(rodeoId) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (rodeoId) api.get(`/rodeos/${rodeoId}/entered`).then((r) => setD(r.data)).catch(() => setD({ counts: {}, total: 0 }));
  }, [rodeoId]);
  return d;
}

// Per-event purse: added money + (entry fee × entered). Totals at the bottom.
export default function PurseBreakdown({ rodeo, entered, dark = false, testid = "purse-breakdown" }) {
  const added = rodeo?.purses || [];
  const fees = rodeo?.entry_fees || [];
  const counts = entered?.counts || {};
  const evs = [];
  const seen = new Set();
  [...added.map((p) => p.event), ...fees.map((p) => p.event), ...Object.keys(counts)].forEach((e) => {
    if (!seen.has(k(e))) { seen.add(k(e)); evs.push(e); }
  });
  if (!evs.length && !rodeo?.added_money) return null;
  const rows = evs.map((e) => {
    const a = Number((added.find((p) => k(p.event) === k(e)) || {}).amount) || 0;
    const f = Number((fees.find((p) => k(p.event) === k(e)) || {}).amount) || 0;
    const n = counts[k(e)] ?? null;
    return { event: k(e), added: a, fee: f, n, total: a + (n != null ? f * n : 0) };
  });
  const sum = (key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
  const addedTotal = sum("added") || Number(rodeo?.added_money) || 0;
  const enteredTotal = entered?.total || 0;
  const purseTotal = rows.length ? sum("total") : addedTotal;
  const c = dark
    ? { box: "border-white/10", head: "text-slate-500", row: "border-white/5", txt: "text-slate-300", strong: "text-slate-100", tot: "bg-white/5" }
    : { box: "border-[#E4E7EB]", head: "text-slate-400", row: "border-[#EEF0F2]", txt: "text-slate-600", strong: "text-[#14181F]", tot: "bg-[#F7F8FA]" };
  return (
    <div className={`rounded-lg border p-3 sm:p-4 ${c.box}`} data-testid={testid}>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Big label="Total purse" value={money(purseTotal)} accent testid={`${testid}-total-purse`} c={c} />
        <Big label="Added money" value={money(addedTotal)} testid={`${testid}-total-added`} c={c} />
        <Big label="Total entered" value={entered ? (enteredTotal || "—") : "…"} testid={`${testid}-total-entered`} c={c} />
      </div>
      {rows.length > 0 && (
        <table className="w-full text-[13px]">
          <thead>
            <tr className={`text-[10px] font-bold uppercase tracking-wide ${c.head}`}>
              <th className="text-left py-1">Event</th>
              <th className="text-right py-1">Entered</th>
              <th className="text-right py-1">Added</th>
              <th className="text-right py-1 hidden sm:table-cell">Fee</th>
              <th className="text-right py-1">Purse</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.event} className={`border-t ${c.row}`} data-testid={`${testid}-row-${r.event.replace(/\s/g, "-")}`}>
                <td className={`py-1.5 ${c.txt}`}>{r.event}</td>
                <td className={`py-1.5 text-right ${c.txt}`}>{r.n ?? "—"}</td>
                <td className={`py-1.5 text-right ${c.txt}`}>{r.added ? money(r.added) : "—"}</td>
                <td className={`py-1.5 text-right hidden sm:table-cell ${c.txt}`}>{r.fee ? money(r.fee) : "—"}</td>
                <td className={`py-1.5 text-right font-semibold ${c.strong}`}>{r.total ? money(r.total) : "—"}</td>
              </tr>
            ))}
            <tr className={`border-t font-bold ${c.row} ${c.tot}`}>
              <td className={`py-1.5 px-1 ${c.strong}`}>Total</td>
              <td className={`py-1.5 text-right ${c.strong}`}>{enteredTotal || "—"}</td>
              <td className={`py-1.5 text-right ${c.strong}`}>{money(addedTotal)}</td>
              <td className="hidden sm:table-cell" />
              <td className="py-1.5 text-right text-[#E68A2E]">{money(purseTotal)}</td>
            </tr>
          </tbody>
        </table>
      )}
      <p className={`text-[11px] mt-2 ${c.head}`}>Purse = added money + entry fees × entered{entered && !entered.from_draw ? " · entered counts appear once the draw is posted" : ""}. Estimate before office fees.</p>
    </div>
  );
}

function Big({ label, value, accent, testid, c }) {
  return (
    <div className={`rounded-md px-2.5 py-2 ${c.tot}`} data-testid={testid}>
      <div className={`text-[10px] font-bold uppercase tracking-wide ${c.head}`}>{label}</div>
      <div className={`text-base font-extrabold mt-0.5 ${accent ? "text-[#E68A2E]" : c.strong}`}>{value}</div>
    </div>
  );
}
